import { useEffect, useMemo, useState } from 'react';
import { AnyObject } from '@triare/auth-redux';
import { App } from 'antd';
import { DefaultOptionType } from 'rc-select/lib/Select';
import { SortOrder } from 'antd/es/table/interface';
import { FetchCreate, FetchDelete, FetchGet, FetchUpdate, getMessageInError, PagingDataResponse } from './fetch';

export interface Item extends DefaultOptionType {
  key?: string | string[];
  color?: string;
}

export function sortByDisable(list: Item[]): Item[] {
  return [...list.filter(({ disabled }) => !disabled), ...list.filter(({ disabled }) => disabled)];
}

export type SelectData = string[] | AnyObject[] | PagingDataResponse<string[] | AnyObject[]>;

export function selectData<D = SelectData>(
  resource: D,
  decorateData?: (data: AnyObject, id: number, item: Item) => Item,
  defaultList?: Item[],
) {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const data = Array.isArray(resource) ? resource : resource?.data;

  if (data) {
    return data.map((value: string | AnyObject, id: number) => {
      if (typeof value === 'string') {
        return {
          label: value,
          key: value,
          value,
        };
      }

      const key = value?.ids || value?.id || value?.key || value?.value;
      const result: Item = {
        label: value?.name || value?.title || value?.description || value?.value,
        value: key,
        key,
      };

      if (decorateData) {
        return decorateData(data, id, result);
      }

      return result;
    }) as Item[];
  }

  return defaultList || [];
}

export function useSelectData<D = SelectData>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  resource?: FetchGet<D, any, any, any>,
  decorateData?: (data: AnyObject, id: number, item: Item) => Item,
  defaultList?: Item[],
): Item[] {
  return useMemo(() => {
    if (resource && !resource.error && resource.data) {
      return selectData(resource.data, decorateData, defaultList);
    }

    return defaultList || [];
  }, [resource?.data, defaultList]);
}

export function useMessageError(listFetch: (FetchCreate | FetchGet | FetchUpdate | FetchDelete)[]) {
  const { message } = App.useApp();

  useEffect(() => {
    for (const fetch of listFetch) {
      if (fetch.error) {
        message.error(
          <span
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: fetch.name ? `${fetch.name}: ${getMessageInError(fetch.error)}` : getMessageInError(fetch.error),
            }}
          />,
        );
        fetch.clearError();
      }
    }
  }, [...listFetch.map((fetch) => fetch.error)]);
}

export function useLoadingTimeout(resources: FetchGet[], time = 500, defaultState = true) {
  const [loading, setLoading] = useState(defaultState);

  useEffect(
    () => {
      let id: NodeJS.Timeout | undefined;

      if (resources.some((resource) => resource.loading)) {
        setLoading(true);
      } else {
        id = setTimeout(() => {
          setLoading(false);
        }, time);
      }

      return () => clearTimeout(id);
    },
    resources.map((resource) => resource.loading),
  );

  return loading;
}

export const getSorterParams = (sorter?: Record<string, SortOrder>): { [key: string]: string } => {
  const sorterEntries = Object.entries(sorter || {});
  const newParams: { [key: string]: string } = {};

  if (sorterEntries.length > 0) {
    newParams.orderByColumn = sorterEntries[0][0] || '';
    newParams.orderBy = sorterEntries[0][1] === 'ascend' ? 'ASC' : 'DESC';
  } else {
    newParams.orderByColumn = '';
    newParams.orderBy = '';
  }

  return queryFilterParams(newParams);
};

export const queryFilterParams = (queryParams: Record<string, string>): Record<string, string> => {
  const params = Object.entries(queryParams);
  const newParams: { [key: string]: string } = {};

  params.forEach(([key, value]) => {
    if (value) {
      newParams[key] = value.toString();
    }
  });

  return newParams;
};

export default {
  useSelectData,
  useLoadingTimeout,
  useMessageError,
};
